import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button as MuiButton,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { ArrowLeft, BarChart2, ListChecks, Lock, Zap } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { Button } from '../../../../../../ui';
import {
  useCreateOrUpdateCCAConfigMutation,
  useGetCCAConfigQuery,
  useLazyListCCAQuestionsQuery,
  useUpdateCCAConfigQuestionsMutation,
} from '../../../../../../utils/api_service/ccaApi';
import { RootState } from '../../../../../../utils/redux_store/store';
import { CCAQuestion } from '../../../../../../utils/types/cca_types';
import { CCAResultsSection } from './CCAResultsSection';
import { AssignedQuestionsList } from './components/AssignedQuestionsList';
import { ManualSelectionDrawer } from './components/ManualSelectionDrawer';
import { QuickBuildDrawer } from './components/QuickBuildDrawer';

const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
  if (!feature) return false;
  return Number(feature.endDate) * 1000 > Date.now();
};

type OpenDrawer = 'quick-build' | 'manual' | null;

export interface ICCATabProps {
  setActiveTab: (tab: string) => void;
}

export const CCATab: React.FC<ICCATabProps> = ({ setActiveTab }) => {
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const hasCCALicense = isLicenseActive(subscriptions?.hierTalentCca);

  const { id: jobOpeningId = '' } = useParams<{ id: string }>();
  const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(null);
  const [showResults, setShowResults] = useState(false);

  const { data: existingConfig, isLoading: configLoading } =
    useGetCCAConfigQuery(jobOpeningId, { skip: !jobOpeningId });

  const [updateQuestions] = useUpdateCCAConfigQuestionsMutation();
  const [createOrUpdateConfig, { isLoading: isTogglingEnabled }] =
    useCreateOrUpdateCCAConfigMutation();

  const ccaEnabled = existingConfig?.enabled ?? false;

  const handleToggleEnabled = async () => {
    const newEnabled = !ccaEnabled;
    try {
      await createOrUpdateConfig({
        jobOpeningId,
        body: {
          enabled: newEnabled,
          ...(existingConfig?.quickConfig
            ? { quickConfig: existingConfig.quickConfig }
            : {}),
        },
      }).unwrap();
    } catch {
      toast.error(
        `Failed to ${newEnabled ? 'enable' : 'disable'} CCA.`,
        { position: 'bottom-center' },
      );
    }
  };

  // Fetch question texts for the assigned questions list
  const [fetchQuestions] = useLazyListCCAQuestionsQuery();
  const [questionMap, setQuestionMap] = useState<Record<string, CCAQuestion>>(
    {},
  );
  const [isLoadingTexts, setIsLoadingTexts] = useState(false);

  useEffect(() => {
    const assigned = existingConfig?.questions;
    if (!assigned?.length) {
      setQuestionMap({});
      return;
    }
    setIsLoadingTexts(true);
    fetchQuestions({ page: 1, page_size: 500 })
      .unwrap()
      .then((result) => {
        const map: Record<string, CCAQuestion> = {};
        for (const q of result.items) map[q.id] = q;
        setQuestionMap(map);
      })
      .catch(() => {})
      .finally(() => setIsLoadingTexts(false));
  }, [existingConfig?.questions]); // eslint-disable-line

  const ccaConfigured =
    !configLoading &&
    !!existingConfig &&
    ((existingConfig.questions?.length ?? 0) > 0 ||
      (existingConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const assignedQuestions = existingConfig?.questions ?? [];

  const handleToggleDrawer = (drawer: 'quick-build' | 'manual') => {
    setOpenDrawer((prev) => (prev === drawer ? null : drawer));
    if (showResults) setShowResults(false);
  };

  const handleRemoveQuestion = async (questionId: string) => {
    const remaining = (existingConfig?.questions ?? [])
      .filter((q) => q.questionId !== questionId)
      .map((q, idx) => ({
        questionId: q.questionId,
        orderKey: idx + 1,
        timeLimitSeconds: q.timeLimitSeconds,
      }));
    try {
      await updateQuestions({
        jobOpeningId,
        body: { questions: remaining },
      }).unwrap();
    } catch {
      toast.error('Failed to remove question.', { position: 'bottom-center' });
    }
  };

  const handleClearAll = async () => {
    try {
      await updateQuestions({ jobOpeningId, body: { questions: [] } }).unwrap();
    } catch {
      toast.error('Failed to clear questions.', { position: 'bottom-center' });
    }
  };

  if (!hasCCALicense) {
    return (
      <Box sx={{ minHeight: 580, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={0} sx={{ textAlign: 'center', maxWidth: 400, p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              mb: 2,
              color: 'text.secondary',
            }}
          >
            <Lock size={28} />
          </Box>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            HierTalent CCA License Required
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
            Your organization does not have an active CCA license. Contact your account manager to enable Candidate Competency Assessments.
          </Typography>
          <MuiButton
            component='a'
            href='mailto:sales@hiacuity.com'
            variant='contained'
            color='primary'
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Contact Sales
          </MuiButton>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 580 }}>
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Switch
            checked={ccaEnabled}
            onChange={handleToggleEnabled}
            disabled={configLoading || isTogglingEnabled}
            color='success'
          />
          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              color: ccaEnabled ? 'success.dark' : 'text.secondary',
            }}
          >
            {configLoading
              ? 'Loading…'
              : ccaEnabled
                ? 'CCA Enabled'
                : 'CCA Disabled'}
          </Typography>
          {!configLoading && !ccaEnabled && (
            <Typography variant='caption' sx={{ color: 'text.disabled' }}>
              Enable CCA and configure questions to assess candidates.
            </Typography>
          )}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {ccaConfigured && (
            <MuiButton
              variant={showResults ? 'contained' : 'outlined'}
              size='small'
              startIcon={<BarChart2 size={14} />}
              onClick={() => {
                setShowResults((v) => !v);
                setOpenDrawer(null);
              }}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Results
            </MuiButton>
          )}
          <MuiButton
            variant={openDrawer === 'quick-build' ? 'contained' : 'outlined'}
            color='success'
            size='small'
            startIcon={<Zap size={14} />}
            onClick={() => handleToggleDrawer('quick-build')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Quick Build
          </MuiButton>
          <MuiButton
            variant={openDrawer === 'manual' ? 'contained' : 'outlined'}
            color='primary'
            size='small'
            startIcon={<ListChecks size={14} />}
            onClick={() => handleToggleDrawer('manual')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Manual Selection
          </MuiButton>
        </Box>
      </Box>

      {/* ── Main content ─────────────────────────────────────────── */}
      {showResults ? (
        <CCAResultsSection jobOpeningId={jobOpeningId} />
      ) : (
        <AssignedQuestionsList
          assignedQuestions={assignedQuestions}
          questionMap={questionMap}
          isLoading={configLoading || isLoadingTexts}
          onRemove={handleRemoveQuestion}
          onClearAll={assignedQuestions.length > 0 ? handleClearAll : undefined}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <MuiButton
          onClick={() => setActiveTab('preQualifyingQuestions')}
          startIcon={<ArrowLeft size={16} />}
          sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary' }}
        >
          Back to Pre-Qualifying Questions
        </MuiButton>
        <Button
          onClick={() => setActiveTab('preview')}
          variant='primary'
          label='Next'
          sx={{ px: 5, boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.2)' }}
        />
      </Box>

      {/* ── Quick Build Drawer ───────────────────────────────────── */}
      <QuickBuildDrawer
        isOpen={openDrawer === 'quick-build'}
        onClose={() => setOpenDrawer(null)}
        jobOpeningId={jobOpeningId}
        existingConfig={existingConfig}
      />

      {/* ── Manual Selection Drawer ──────────────────────────────── */}
      <ManualSelectionDrawer
        isOpen={openDrawer === 'manual'}
        onClose={() => setOpenDrawer(null)}
        jobOpeningId={jobOpeningId}
        existingConfig={existingConfig}
      />
    </Box>
  );
};

export default CCATab;
